import { api } from '@/store/helpers'
import actions from '@/store/helpers/actions'
import Application from 'App/Foundation/Application'
import {
	SET_ACTIVE,
	SET_PENDING_CALLS_COUNT,
	SET_PENDING_CALLS_COUNT_BY_ID,
	UPDATE_PAGINATION_CONFIG,
} from '@/store/mutation-types'

/**
 * The module's dispatchable actions.
 *
 * @author Erik Galloway <egalloway@claruscare.com>
 */
export default {
	...actions,

	/**
	 * Decrement the partner's pending calls count.
	 *
	 * @param {Function} options.commit
	 * @param {Object} options.state
	 * @return {void}
	 */
	decrementPendingCallsCount({ commit, state }) {
		const partner = state.items[state.active]

		const current = partner?.pending_calls_count || 0

		commit(SET_PENDING_CALLS_COUNT, current - 1)
	},

	/**
	 * Find a partner by id.
	 *
	 * @param {Function} options.dispatch
	 * @param {Number} id
	 * @return {void}
	 */
	async find({ dispatch }, id) {
		let response = await api()
			.partners()
			.find(id)

		const { migrated_partner_id: migratedPartnerId } = response.get(
			'data',
			{}
		)
		if (migratedPartnerId && migratedPartnerId !== id) {
			response = await api()
				.partners()
				.find(migratedPartnerId)
		}

		let { providers = [], calendars = [], ...partner } = response.get(
			'data',
			{}
		)

		calendars = calendars.map(calendar => ({ ...calendar, events: [] }))

		await dispatch('calendars/add', calendars, { root: true })
		await dispatch('providers/set', providers, { root: true })

		await dispatch('addOrMerge', partner)

		return response
	},

	/**
	 * Get all the partners.
	 *
	 * @param {Function} options.dispatch
	 * @param {Object} options.state
	 * @return {Promise}
	 */
	async get({ dispatch, state }) {
		const response = await api()
			.partners()
			.get()

		const data = response.get('data', [])

		const defaultPartnerId = data.length ? data[0].id : null

		const activePartnerId = state.active

		await dispatch('addOrMerge', data)

		if (!activePartnerId) {
			await dispatch('setActive', defaultPartnerId)
		}

		if (activePartnerId) {
			await dispatch('find', activePartnerId)
		}

		return response
	},

	async getPartnerList({ dispatch, rootState, state, commit }, payload) {
		const search = payload?.search ?? false
		commit(UPDATE_PAGINATION_CONFIG, { isSearching: search })
		const page = rootState.partners.pagination.current_page || 1
		const keyword = rootState.partners.pagination.searchKeyword || ''

		const response = await api()
			.partners()
			.page(page)
			.search(keyword)
			.get()

		const res = response.get('pagination', {})
		if (res?.last_page !== rootState.partners.pagination.last_page) {
			commit(UPDATE_PAGINATION_CONFIG, { last_page: res?.last_page })
		}

		const data = response.get('partners', [])

		if (page === 1 && keyword === '') {
			let myData = [...data, state.items[state.active]]
			if (payload?.selectedPartner) {
				myData.push({
					...payload.selectedPartner,
					name: payload.selectedPartner?.label,
				})
			}
			await dispatch('set', myData)
		} else {
			await dispatch('addOrMerge', data)
		}
		return response
	},

	/**
	 * Increment the partner's pending calls count.
	 *
	 * @param {Function} options.commit
	 * @param {Object} options.state
	 * @return {void}
	 */
	incrementPendingCallsCount({ commit, state }) {
		const partner = state.items[state.active]

		const current = partner?.pending_calls_count || 0

		commit(SET_PENDING_CALLS_COUNT, current + 1)
	},

	incrementPendingCallsCountById({ commit }, callCount) {
		commit(SET_PENDING_CALLS_COUNT_BY_ID, callCount)
	},

	/**
	 * Set the active partener.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} options.state
	 * @param {Number} id
	 * @return {void}
	 */
	async setActive({ commit, dispatch, state }, id) {
		let resolvedId = id
		if (id) {
			const response = await dispatch('find', id)
			resolvedId = response?.get('data', {})?.id ?? id
		}

		if (state.active && state.active !== resolvedId) {
			Application.getInstance().emit('partner:change', {
				leave: state.active,
				subscribe: resolvedId,
			})
		}

		if (state.active !== resolvedId) {
			commit(SET_ACTIVE, resolvedId)
		}
	},

	/**
	 * Update the partner.
	 *
	 * @param {Function} options.dispatch
	 * @param {Object} partner
	 * @return {Promise}
	 */
	async update({ dispatch, state }, partner) {
		const response = await api()
			.partners()
			.update(partner)
		const payload = response.get('partner')
		const ehrConfig = response.get('ehr_config')
		const activePartnerId = state.active
		const existingEhrConfig = state.items[activePartnerId].ehr_settings

		if (response.get('ehr_config')) {
			payload.ehr_settings = {
				...existingEhrConfig,
				...ehrConfig,
				note_types: JSON.stringify(ehrConfig.note_types),
				partner_id: response.get('partner').id,
			}
		}
		dispatch('addOrMerge', payload)

		return response
	},

	async setForwardingNumber({ state }, payload) {
		const { id, phone_number } = payload
		if (state.active !== id) {
			return
		}
		const requestPayload = {
			forwarding_number: phone_number,
		}
		try {
			const response = await api()
				.partners()
				.setForwardingNumber(id, requestPayload)
			return response
		} catch (error) {
			return error
		}
	},
}
